import React, { useState } from 'react';
import { HandDrawnButton } from '../HandDrawnButton/HandDrawnButton';
import './FilterCategory.css';

export interface FilterCategoryProps {
  label: string;
  active?: boolean;
  onClick: () => void;
  level?: 'brand' | 'atacama' | 'patagonia' | 'iguazu' | 'mendoza' | 'santa-catarina';
  variant?: 'default' | 'compact' | 'minimal';
  size?: 'small' | 'normal' | 'large';
  className?: string;
  disabled?: boolean;
  count?: number; // Optional count badge
}

/**
 * FilterCategory Component - Figma-exact implementation
 * Single filter button with property-specific theming and hover states
 * Used for category filters, tags, and individual filter options
 */
export const FilterCategory: React.FC<FilterCategoryProps> = ({
  label,
  active = false,
  onClick,
  level = 'brand',
  variant = 'default',
  size = 'normal',
  className = '',
  disabled = false,
  count
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = () => {
    if (!disabled) {
      onClick();
    }
  };

  // Determine button animation based on current state
  const getAnimation = (): 'stroke-to-fill' | 'fill-to-stroke' | 'stroke-to-stroke' => {
    if (active) {
      return 'fill-to-stroke'; // Active state shows filled, hovers to stroke
    }
    return 'stroke-to-fill'; // Inactive state shows stroke, hovers to fill
  };

  // Determine button color based on level and state
  const getColor = (): 'awasi' | 'off-white' | 'white' => {
    if (level === 'brand') {
      return active ? 'awasi' : 'awasi';
    }
    return 'awasi'; // All property themes use awasi color with level-specific styling
  };

  // Build display text with optional count
  const displayText = count ? `${label} (${count})` : label;

  return (
    <div 
      className={`filter-category filter-category--${level} filter-category--${variant} filter-category--${size} ${active ? 'filter-category--active' : ''} ${disabled ? 'filter-category--disabled' : ''} ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <HandDrawnButton
        text={displayText}
        onClick={handleClick}
        variant={level === 'brand' ? 'brand' : level}
        color={getColor()}
        animation={getAnimation()}
        size={size === 'small' ? 'normal' : size === 'large' ? 'wide' : 'normal'}
        className={`filter-category__button ${active ? 'filter-category__button--active' : ''}`}
      />
      
      {/* Active indicator */}
      {active && (
        <div className="filter-category__indicator" />
      )}
    </div>
  );
};

export default FilterCategory;